/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonItem } from '@atlaskit/menu';
import { Divider, MenuList, MenuSection, MenuSectionHeading } from '@atlaskit/navigation-system';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { FoldableType } from '../../../types';
import ItemWrapper from '../MenuItem/item-wrapper';
import ShowMoreButton, { showMoreMessages } from '../ShowMoreButton';

import { hasEnhancedMoreButton, hasMoreButton } from './utils';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},

	textStyle: {
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		color: token('color.text.subtle'),
	},
});

const restProps = {
	className: 'custom-item-style',
};

export type MenuSectionProps = {
	children: React.ReactNode;
	compact?: boolean;
	foldable?: FoldableType;
	heading?: string;
	isNative?: boolean;
	itemLength: number;
	sectionIndex: number;
	showAll: boolean;
	toggleShowMore: () => void;
};

export default function MenuSectionPrimitive({
	heading,
	sectionIndex,
	itemLength,
	foldable,
	compact = false,
	isNative = true,
	toggleShowMore,
	children,
	showAll,
}: MenuSectionProps) {
	const { formatMessage } = useIntl();

	const text = showAll
		? formatMessage(showMoreMessages.showLess)
		: formatMessage(showMoreMessages.showMore);

	if (!itemLength) {
		return null;
	}

	// when compact is true, it should hide both heading and divider
	// when compact is false or the section is native, it should only show the divider if it is not the first section
	const shouldShowDivider = (isNative || !compact) && sectionIndex !== 0;
	const shouldShowHeading = !!(heading && (isNative || !compact));

	return (
		<MenuSection key={`menu-section-${sectionIndex}`}>
			{shouldShowDivider && <Divider />}
			{shouldShowHeading && (
				<ItemWrapper isMenuItem={false}>
					<MenuSectionHeading>{heading}</MenuSectionHeading>
				</ItemWrapper>
			)}
			{/* A11y: Wrap only menu items inside MenuList for proper semantic structure */}
			<MenuList>{children}</MenuList>
			{hasMoreButton(foldable, itemLength) &&
				(hasEnhancedMoreButton(foldable, itemLength) ? (
					<ShowMoreButton
						onClick={toggleShowMore}
						heading={heading}
						showAll={showAll}
						foldable={foldable}
					/>
				) : (
					<ItemWrapper isMenuItem={false}>
						<ButtonItem
							{...restProps}
							iconBefore={<ShowMoreHorizontalIcon label="" color="var(--ds-text-subtle)" />}
							onClick={toggleShowMore}
							testId="toggle-more-less-button"
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
							aria-label={`Toggle for ${text}${heading ? ` ${heading}` : ''} options`}
						>
							<Box as="span" xcss={styles.textStyle}>
								{text}
							</Box>
						</ButtonItem>
					</ItemWrapper>
				))}
		</MenuSection>
	);
}
